/**
 * Authentication Service
 * Handles API communication for authentication endpoints
 */

import { tokenManager } from '../utils/tokenManager'

export interface User {
  id: number
  email: string
  username: string
  first_name: string
  last_name: string
  is_active: boolean
  two_factor_enabled: boolean
  created_at: string
  updated_at: string
}

export interface Group {
  id: number
  external_id: string
  name: string
  description: string
  owner_id: number
  owner_type: string
  is_personal: boolean
}

export interface UserActiveGroup {
  active_group: Group,
}

export interface UserPreferences {
  active_group_id: number,
  active_group: Group,
  onboarding_step: number,
  onboarding_done: boolean,
  updated_at: string
}

export interface OnboardingUpdate {
  onboarding_step?: number,
  onboarding_done?: boolean,
}

export interface LoginResponse {
  access_token: string
  refresh_token: string
  user: User
}

export interface RefreshResponse {
  access_token: string
  refresh_token: string
}

export interface RegisterData {
  email: string
  password: string
  first_name: string
  last_name: string
}

// User Profile Management Types
export interface UpdateProfileData {
  first_name: string
  last_name: string
  username?: string
  email?: string
}

export interface ChangePasswordData {
  current_password: string
  new_password: string
}

export interface DeleteAccountData {
  password: string
}

// 2FA Management Types
export interface TwoFactorStatus {
  enabled: boolean
}

export interface TwoFactorSetupResponse {
  secret: string
  qr_code_url: string
  qr_code_image: string
  backup_codes: string[]
}

export interface TwoFactorVerification {
  code: string
}

export interface TwoFactorDisable {
  password: string
  code: string
}

export interface SuccessResponse {
  message: string
}

export interface AuthService {
  // Authentication
  login(email: string, password: string): Promise<LoginResponse>
  register(userData: RegisterData): Promise<void>
  logout(): Promise<void>
  refreshToken(refreshToken: string): Promise<RefreshResponse>

  // User Profile Management
  getUserProfile(): Promise<User>
  updateUserProfile(userData: UpdateProfileData): Promise<User>
  changePassword(passwordData: ChangePasswordData): Promise<SuccessResponse>
  deleteAccount(accountData: DeleteAccountData): Promise<SuccessResponse>

  // Two-Factor Authentication
  get2FAStatus(): Promise<TwoFactorStatus>
  enable2FA(): Promise<TwoFactorSetupResponse>
  verify2FA(verification: TwoFactorVerification): Promise<SuccessResponse>
  disable2FA(disableData: TwoFactorDisable): Promise<SuccessResponse>

  // Password Reset
  initiatePasswordReset(email: string): Promise<{ success: boolean; requires_2fa: boolean; message: string }>
  verifyPasswordReset(email: string, resetToken: string, totpCode?: string): Promise<{ temporary_token: string; success: boolean; message: string }>
  completePasswordReset(email: string, temporaryToken: string, newPassword: string): Promise<SuccessResponse>

  // Groups
  getUserGroups(): Promise<Group[]>

  //Preferences
  getUserPreferences(): Promise<UserPreferences>
  getUserActiveGroup(): Promise<UserActiveGroup>
  setUserActiveGroup(groupID: number): Promise<UserPreferences>
  updateOnboarding(data: OnboardingUpdate): Promise<UserPreferences>
}

/**
 * HTTP-based Authentication Service Implementation
 */
class AuthServiceImpl implements AuthService {
  private readonly baseUrl = '/v1/auth'
  private readonly userBaseUrl = '/v1/user'

  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {},
    useUserBase = false
  ): Promise<T> {
    const baseUrl = useUserBase ? this.userBaseUrl : this.baseUrl
    const url = `${baseUrl}${endpoint}`
    
    // Enhanced debug logging
    console.group(`[AuthService] ${options.method || 'GET'} ${url}`)
    console.log('Full URL:', url)
    console.log('Headers:', options.headers)
    console.log('Body:', options.body)
    console.log('UseUserBase:', useUserBase)
    
    const defaultHeaders = {
      'Content-Type': 'application/json',
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    }

    try {
      const response = await fetch(url, config)
      
      console.log('Response Status:', response.status)
      console.log('Response Headers:', response.headers)
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.log('Error Data:', errorData)
        console.groupEnd()
        throw new AuthError(
          errorData.message || `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData
        )
      }

      // Handle 204 No Content responses (like logout)
      if (response.status === 204) {
        console.log('No Content Response')
        console.groupEnd()
        return {} as T
      }

      const data = await response.json()
      console.log('Response Data:', data)
      console.groupEnd()
      return data
    } catch (error) {
      console.log('Request Error:', error)
      console.groupEnd()
      
      if (error instanceof AuthError) {
        throw error
      }
      
      // Network or other errors
      throw new AuthError(
        'Network error occurred. Please check your connection.',
        0,
        { originalError: error }
      )
    }
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    return this.makeRequest<LoginResponse>('/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  }

  async register(userData: RegisterData): Promise<void> {
    await this.makeRequest<void>('/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    })
  }

  async logout(): Promise<void> {
    await this.makeRequest<void>('/logout', {
      method: 'POST',
    })
  }

  async refreshToken(refreshToken: string): Promise<RefreshResponse> {
    return this.makeRequest<RefreshResponse>('/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
  }

  // Helper method to get current JWT token
  private async getAuthHeaders(): Promise<HeadersInit> {
    try {
      const token = await tokenManager.getAccessToken()
      
      if (!token) {
        throw new AuthError('No access token available', 401)
      }
      
      return {
        'Authorization': `Bearer ${token}`
      }
    } catch (error) {
      throw new AuthError('Authentication required', 401)
    }
  }

  // User Profile Management Methods
  async getUserProfile(): Promise<User> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<User>('/profile', {
      method: 'GET',
      headers: authHeaders,
    }, true)
  }

  async updateUserProfile(userData: UpdateProfileData): Promise<User> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<User>('/profile', {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify(userData),
    }, true)
  }

  async changePassword(passwordData: ChangePasswordData): Promise<SuccessResponse> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<SuccessResponse>('/change-password', {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(passwordData),
    }, true)
  }

  async deleteAccount(accountData: DeleteAccountData): Promise<SuccessResponse> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<SuccessResponse>('/account', {
      method: 'DELETE',
      headers: authHeaders,
      body: JSON.stringify(accountData),
    }, true)
  }

  // Two-Factor Authentication Methods
  async get2FAStatus(): Promise<TwoFactorStatus> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<TwoFactorStatus>('/2fa/status', {
      method: 'GET',
      headers: authHeaders,
    }, true)
  }

  async enable2FA(): Promise<TwoFactorSetupResponse> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<TwoFactorSetupResponse>('/2fa/enable', {
      method: 'POST',
      headers: authHeaders,
    }, true)
  }

  async verify2FA(verification: TwoFactorVerification): Promise<SuccessResponse> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<SuccessResponse>('/2fa/verify', {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(verification),
    }, true)
  }

  async disable2FA(disableData: TwoFactorDisable): Promise<SuccessResponse> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<SuccessResponse>('/2fa/disable', {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify(disableData),
    }, true)
  }

  // Password Reset Methods
  async initiatePasswordReset(email: string): Promise<{ success: boolean; requires_2fa: boolean; message: string }> {
    return this.makeRequest<{ success: boolean; requires_2fa: boolean; message: string }>('/reset/initiate', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  }

  async verifyPasswordReset(email: string, resetToken: string, totpCode?: string): Promise<{ temporary_token: string; success: boolean; message: string }> {
    const body: Record<string, string> = { email, reset_token: resetToken }
    if (totpCode) body.totp_code = totpCode
    return this.makeRequest<{ temporary_token: string; success: boolean; message: string }>('/reset/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async completePasswordReset(email: string, temporaryToken: string, newPassword: string): Promise<SuccessResponse> {
    return this.makeRequest<SuccessResponse>('/reset/complete', {
      method: 'POST',
      body: JSON.stringify({ 
        email,
        temporary_token: temporaryToken, 
        new_password: newPassword,
        confirm_password: newPassword 
      }),
    })
  }

  // User Groups Management Methods
  async getUserGroups(): Promise<Group[]> {
    const authHeaders = await this.getAuthHeaders()
    const response = await this.makeRequest<{ groups: Group[] }>('/preferences/groups', {
      method: 'GET',
      headers: authHeaders,
    }, true)
    // API returns { groups: [...] }, so unwrap it
    return response.groups
  }

  // Preferences Management Methods
  async getUserPreferences(): Promise<UserPreferences> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<UserPreferences>('/preferences', {
      method: 'GET',
      headers: authHeaders,
    }, true)
  }

  async getUserActiveGroup(): Promise<UserActiveGroup> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<UserActiveGroup>('/preferences/active-group', {
      method: 'GET',
      headers: authHeaders,
    }, true)
  }

  async setUserActiveGroup(groupId: number): Promise<UserPreferences> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<UserPreferences>('/preferences/active-group', {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify({
        group_id: groupId
      }),
    }, true)
  }

  async updateOnboarding(data: OnboardingUpdate): Promise<UserPreferences> {
    const authHeaders = await this.getAuthHeaders()
    return this.makeRequest<UserPreferences>('/preferences/onboarding', {
      method: 'PUT',
      headers: authHeaders,
      body: JSON.stringify(data),
    }, true)
  }
}

/**
 * Custom Authentication Error Class
 */
export class AuthError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly data?: any
  ) {
    super(message)
    this.name = 'AuthError'
  }

  get isNetworkError(): boolean {
    return this.status === 0
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isValidationError(): boolean {
    return this.status === 422 || this.status === 400
  }
}

// Export singleton instance
export const authService = new AuthServiceImpl()